import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Globe } from 'lucide-react';
import { LANGUAGES, LANGUAGE_NAMES, Language, currentLanguage } from '@/i18n/format';
import { setLanguage } from '@/i18n';

/**
 * The language picker: NL, EN, DE, TR, the same everywhere (map, sign-in, admin, users). The choice is kept
 * in the browser (localStorage 'language') and written to <html lang>.
 */
export const LanguagePicker: React.FC<{ className?: string; align?: 'left' | 'right' }> = ({ className, align = 'right' }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const lang = currentLanguage();

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const choose = (l: Language) => {
    setLanguage(l);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-lang={i18n.language}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={className ?? 'flex items-center gap-1 h-9 px-2.5 rounded-lg text-[11px] font-medium text-gn-muted bg-gn-surface border border-gn-light hover:bg-gn-tertiary backdrop-blur-sm shadow-lg transition-colors'}
        title={t('menu.language')}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe size={14} className="text-gn-secondary" />
        {lang.toUpperCase()}
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label={t('menu.language')}
          className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full mt-1 z-[60] min-w-[150px] rounded-lg border border-gn-light bg-gn-surface shadow-xl overflow-hidden`}
        >
          {LANGUAGES.map((l) => (
            <li key={l}>
              <button
                type="button"
                role="option"
                aria-selected={l === lang}
                lang={l}
                onClick={() => choose(l)}
                className="flex items-center justify-between gap-3 w-full px-3 py-2 text-[12px] text-gn-primary hover:bg-gn-tertiary"
              >
                <span>{LANGUAGE_NAMES[l]}</span>
                <span className="flex items-center gap-1 text-[10px] text-gn-muted">
                  {l.toUpperCase()}
                  {l === lang && <Check size={12} className="text-gn-blue" />}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguagePicker;
